/**
 * Channel visibility for the things a guest can ORDER — items, the per-group
 * item reference, modifier groups, modifiers, and the parent→child sub-group
 * edge. The item-level companion of `menuGroupAvailability.ts`, which already
 * answers the same question for menu groups.
 *
 * WHY THIS FILE EXISTS. Every guest-facing surface used to decide item
 * visibility on its own, and almost none of them read the channel flag. The
 * management app's Visibility tab writes `onlineOrdering: false` on the item
 * document, and the storefront (`MenuPageClient`), the featured-items band,
 * the machine-readable menu, the AI menu tools and server-side checkout all
 * ignored it — measured on House of Pho production 2026-09-16: "Bowl Add On"
 * stored `onlineOrdering: false` and still rendered, opened and ordered on
 * houseofphoct.com/menu. The marketplace publisher read its own flag, which is
 * why the owner saw partners behave and the website not. One predicate per
 * question, read by every surface, is the fix — a second copy is how the first
 * one went unread.
 *
 * 🔴 ABSENT IS VISIBLE. Every clause below is `=== false` (or `=== true` for a
 * hide flag), never truthiness. Documents written before a flag existed carry
 * no value, and reading that as "off" would silently delist them. Measured on
 * Manje Lakay production 2026-09-17: one of ten modifier groups has no
 * `onlineOrdering` field at all, and it is on the storefront today.
 *
 * Pure functions over the stored shape, so they answer identically for a raw
 * Firestore document and for a hydrated model instance.
 */

import type { IMenuGroup } from './MenuGroup';
import type { IModifierGroupReference } from './ModifierGroupReference';
import type { ISubgroupReference } from './SubgroupReference';
import { isMenuGroupAvailableForDisplay } from './menuGroupAvailability';
import { isInStock, type StockFields } from './stockAvailability';
import type { SalesChannel } from './types';

/** The partner channels — one marketplace flag governs all of them. */
const PARTNER_CHANNELS: ReadonlySet<SalesChannel> = new Set<SalesChannel>([
  'UBER_EATS',
  'DOORDASH',
  'GRUBHUB',
  'POSTMATES',
  'CAVIAR',
  'SEAMLESS',
  'YELP',
  'GOOGLE',
  'FACEBOOK',
  'INSTAGRAM',
]);

/** The channel-bearing fields an item and a modifier group share. */
export interface ChannelFlagFields {
  readonly pos?: boolean;
  readonly kiosk?: boolean;
  readonly onlineOrdering?: boolean;
  readonly customerInvoices?: boolean;
  readonly onlineOrderingPartners?: boolean;
  readonly enabledChannels?: readonly SalesChannel[];
  readonly disabledChannels?: readonly SalesChannel[];
}

/** The two ways the menu marks an item as priced at the till. */
export interface OpenPriceFields {
  readonly pricingStrategy?: string;
  readonly openPrice?: boolean;
}

/** The fields `isItemChannelEnabled` reads. */
export interface ItemChannelFields extends ChannelFlagFields, OpenPriceFields {
  readonly isActive?: boolean;
  readonly isArchived?: boolean;
}

/**
 * Whether the item is OPEN-PRICE — its price is typed at the counter, not set
 * by the menu. Both markers count: `pricingStrategy: 'open'` (what the pricing
 * resolver reads) and the legacy `openPrice` flag (what the marketplace
 * transformers read). Seeding sets both (`SystemItemSeed`) because reading one
 * left half the stack treating the item as fixed-price; this is where the two
 * become one answer.
 */
export function isOpenPriceItem(item: OpenPriceFields): boolean {
  return item.pricingStrategy === 'open' || item.openPrice === true;
}

/**
 * Whether an OPEN-PRICE item is refused on `channel` — everywhere but the
 * counter. OWNER DECISIONS 2026-09-24 (online) and 2026-09-25 (marketplaces):
 * open-price items are TILL ONLY.
 *
 *   - ONLINE_ORDERING: the GUEST would be typing the price — the one number the
 *     server never takes from the browser (spec A1).
 *   - Every PARTNER channel (Uber Eats, DoorDash, KitchenHub's marketplaces,
 *     Google…): a marketplace has no open price, so the item used to be
 *     published at a FIXED floor (`minPrice ?? basePrice`) — a price nobody
 *     set, charged to every marketplace guest. It is now omitted instead.
 *
 * Exported on its own so a marketplace transformer can apply exactly this
 * clause without also adopting the granular channel lists it has never read;
 * {@link isItemChannelEnabled} applies it too, so the two cannot drift.
 */
export function isOpenPriceRefusedOn(
  item: OpenPriceFields,
  channel: SalesChannel,
): boolean {
  if (!isOpenPriceItem(item)) return false;
  return channel === 'ONLINE_ORDERING' || PARTNER_CHANNELS.has(channel);
}

/** The fields `isModifierGroupChannelEnabled` reads. */
export interface ModifierGroupChannelFields extends ChannelFlagFields {
  readonly isActive?: boolean;
  readonly isArchived?: boolean;
}

/**
 * The legacy boolean that governs `channel`, or `undefined` when no flag
 * speaks to it (MOBILE_APP, DINE_IN and the other fulfillment members).
 */
function legacyFlagFor(
  fields: ChannelFlagFields,
  channel: SalesChannel,
): boolean | undefined {
  if (channel === 'POS') return fields.pos;
  if (channel === 'KIOSK') return fields.kiosk;
  if (channel === 'ONLINE_ORDERING') return fields.onlineOrdering;
  if (channel === 'CUSTOMER_INVOICE') return fields.customerInvoices;
  if (PARTNER_CHANNELS.has(channel)) return fields.onlineOrderingPartners;
  return undefined;
}

/**
 * The channel flags alone: the granular lists first (an explicit disable wins;
 * an explicit allow-list excludes everything it does not name), then the
 * legacy boolean. Mirrors `Item.isAvailableOnChannel` member for member, minus
 * its `?? true` construction — which is exactly what makes this safe to call on
 * a raw document.
 */
function channelFlagsAllow(
  fields: ChannelFlagFields,
  channel: SalesChannel,
): boolean {
  if (fields.disabledChannels?.includes(channel)) return false;
  if (fields.enabledChannels && !fields.enabledChannels.includes(channel)) {
    return false;
  }
  return legacyFlagFor(fields, channel) !== false;
}

/**
 * Whether an ITEM may be shown and ordered on `channel`: active, not archived,
 * and not switched off for the channel.
 *
 * An OPEN-PRICE item (`isOpenPriceItem`) is never offered on ONLINE_ORDERING
 * nor on any partner channel ({@link isOpenPriceRefusedOn}).
 *
 * Suspension (86'd) is deliberately NOT part of this: an 86'd item stays on the
 * menu and reads "unavailable" — that is a stock state, not a visibility one.
 */
export function isItemChannelEnabled(
  item: ItemChannelFields,
  channel: SalesChannel,
): boolean {
  if (item.isActive === false) return false;
  if (item.isArchived === true) return false;
  // Open-price items are TILL ONLY — online and on every marketplace.
  if (isOpenPriceRefusedOn(item, channel)) return false;
  return channelFlagsAllow(item, channel);
}

/** `isItemChannelEnabled(item, 'ONLINE_ORDERING')` — the storefront question. */
export function isItemVisibleOnline(item: ItemChannelFields): boolean {
  return isItemChannelEnabled(item, 'ONLINE_ORDERING');
}

/**
 * Whether `itemId` is listed in `group` AND not hidden there. The Menu Group
 * editor's "hide in this group" writes `items[].isHidden`; an item hidden in
 * one group stays orderable through any other group that lists it visibly.
 */
export function isItemListedVisiblyInGroup(
  group: Pick<IMenuGroup, 'items'>,
  itemId: number | null | undefined,
): boolean {
  if (itemId == null) return false;
  return (group.items ?? []).some(
    (ref) => ref.itemId === itemId && ref.isHidden !== true,
  );
}

/** The per-parent override field that governs `channel`, if any. */
function referenceFlagFor(
  ref: IModifierGroupReference,
  channel: SalesChannel,
): boolean | undefined {
  if (channel === 'POS') return ref.visibleInPOS;
  if (channel === 'KIOSK') return ref.visibleInKiosk;
  if (channel === 'ONLINE_ORDERING') return ref.visibleInOnlineOrdering;
  if (channel === 'CUSTOMER_INVOICE') return ref.visibleInInvoices;
  if (PARTNER_CHANNELS.has(channel)) return ref.visibleToPartners;
  return undefined;
}

/**
 * The per-parent modifier-group override that applies along a ROOT→LEAF menu
 * group path: the FIRST group on the path whose `modifierGroups[]` reference
 * for this modifier group says anything about visibility on `channel` (hides it,
 * or sets the channel flag either way). Same first-match-wins walk as
 * `resolveModifierPriceOverrides`, so price and visibility overrides for one
 * modifier group are always read from the same parent.
 *
 * When no group on the path speaks, the ITEM's own reference to the modifier
 * group (`item.modifierGroups[]`) is consulted last — the same precedence as
 * pricing, where the parent menu group outranks the item. Returns `undefined`
 * when nothing overrides it; the modifier group's own flags then decide.
 */
export function resolveModifierGroupVisibilityOverride(
  modifierGroupId: number,
  menuGroupPath: readonly number[] | undefined,
  allMenuGroups: ReadonlyArray<Pick<IMenuGroup, 'menuGroupId' | 'modifierGroups'>>,
  channel: SalesChannel,
  itemReference?: IModifierGroupReference,
): IModifierGroupReference | undefined {
  const speaks = (ref: IModifierGroupReference): boolean =>
    ref.isHidden === true || referenceFlagFor(ref, channel) !== undefined;
  for (const gid of menuGroupPath ?? []) {
    const group = allMenuGroups.find((g) => g.menuGroupId === gid);
    const ref = group?.modifierGroups?.find(
      (r) => r.modifierGroupId === modifierGroupId,
    );
    if (ref && speaks(ref)) return ref;
  }
  if (
    itemReference &&
    itemReference.modifierGroupId === modifierGroupId &&
    speaks(itemReference)
  ) {
    return itemReference;
  }
  return undefined;
}

/**
 * Whether a MODIFIER GROUP may be offered on `channel`.
 *
 * `parentOverride` is the per-parent reference from
 * `resolveModifierGroupVisibilityOverride`. When it hides the group, the group
 * is hidden; when it sets the channel flag, THAT value decides (an operator can
 * re-enable under one parent a group that is off everywhere else); otherwise
 * the modifier group's own flags decide.
 */
export function isModifierGroupChannelEnabled(
  group: ModifierGroupChannelFields,
  channel: SalesChannel,
  parentOverride?: IModifierGroupReference,
): boolean {
  if (group.isActive === false) return false;
  if (group.isArchived === true) return false;
  if (parentOverride?.isHidden === true) return false;
  const overrideFlag = parentOverride
    ? referenceFlagFor(parentOverride, channel)
    : undefined;
  if (overrideFlag !== undefined) return overrideFlag;
  return channelFlagsAllow(group, channel);
}

/**
 * Whether an item OFFERS one of its modifier groups on `channel` when ordered
 * through `menuGroupPath` — the group's own flags, overridden by the first
 * parent menu group on the path that speaks, then by the item's own reference.
 * The one question the storefront dialog, the AI menu tools and server-side
 * checkout all ask, so a group hidden on one cannot be required by another.
 */
export function isModifierGroupOfferedOnItem(
  itemReference: IModifierGroupReference,
  group: ModifierGroupChannelFields,
  menuGroupPath: readonly number[] | undefined,
  allMenuGroups: ReadonlyArray<Pick<IMenuGroup, 'menuGroupId' | 'modifierGroups'>>,
  channel: SalesChannel,
): boolean {
  return isModifierGroupChannelEnabled(
    group,
    channel,
    resolveModifierGroupVisibilityOverride(
      itemReference.modifierGroupId,
      menuGroupPath,
      allMenuGroups,
      channel,
      itemReference,
    ),
  );
}

/**
 * Whether a single MODIFIER may be selected: active and not 86'd. A modifier
 * carries no channel flags of its own — its visibility is its group's.
 *
 * "Not 86'd" is `isInStock`, not a bare `isSuspended !== true`. A modifier
 * carries the SAME stock fields as an item, and the POS's
 * `ModifierAvailabilityCubit` and management's `ModifierModal` both 86 one by
 * writing `alwaysAvailable: false` + `quantityRemaining: 0` — never
 * `isSuspended`, which only the automatic depletion path raises. Reading the
 * flag alone let a hand-86'd option be selected online (House of Pho,
 * 2026-09-19).
 */
export function isModifierSelectable(
  modifier: { readonly isActive?: boolean } & StockFields,
): boolean {
  return modifier.isActive !== false && isInStock(modifier);
}

/**
 * Whether a parent→child sub-group EDGE is open RIGHT NOW, on no particular
 * channel: the parent's `subgroups[]` reference may hide the child under THIS
 * parent, or narrow it to its own time windows. A child hidden under one parent
 * stays reachable through another.
 *
 * The ONE channel-neutral edge rule. `isSubgroupEdgeAvailableOnline` adds the
 * online switches on top of it; `resolveScheduledPath` (which also serves
 * KitchenHub, not an online-ordering channel) calls it directly.
 *
 * `ref` null/undefined means the parent carries no reference for the child (the
 * relationship exists only through `parentIds`), which is no override at all.
 */
export function isSubgroupEdgeOpenNow(
  ref: ISubgroupReference | null | undefined,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!ref) return true;
  if (ref.isHidden === true) return false;

  // Only the time-based override fields participate; when none are set this is
  // vacuously true and the child group's own windows govern.
  if (!ref.daysAvailable && !ref.dateRange && !ref.seasonalAvailability) {
    return true;
  }
  return isMenuGroupAvailableForDisplay(
    {
      daysAvailable: ref.daysAvailable,
      dateRange: ref.dateRange,
      seasonalAvailability: ref.seasonalAvailability,
    } as unknown as IMenuGroup,
    now,
    timeZone,
  );
}

/**
 * The edge's SWITCHES for online ordering, with no clock: not hidden under this
 * parent, and neither online switch (`visibleInOnlineOrdering`,
 * `availability.online`) off. The time-independent half of
 * {@link isSubgroupEdgeAvailableOnline}, for a reader that cannot know the
 * restaurant's clock (a server with no zone for it) and must still refuse what
 * no clock can open.
 */
export function isSubgroupEdgeEnabledOnline(ref: ISubgroupReference | null | undefined): boolean {
  if (!ref) return true;
  if (ref.isHidden === true) return false;
  if (ref.visibleInOnlineOrdering === false) return false;
  if (ref.availability && ref.availability.online === false) return false;
  return true;
}

/**
 * Whether a parent→child sub-group EDGE is open for online ordering right now:
 * the edge's online switches ({@link isSubgroupEdgeEnabledOnline}), then the
 * channel-neutral {@link isSubgroupEdgeOpenNow}.
 */
export function isSubgroupEdgeAvailableOnline(
  ref: ISubgroupReference | undefined,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!isSubgroupEdgeEnabledOnline(ref)) return false;
  return isSubgroupEdgeOpenNow(ref, now, timeZone);
}
